import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button.js";
import { verifyMagicLink } from "@/lib/license-api.js";
import { setSession } from "@/lib/session.js";
import { setDek } from "@/lib/dek.js";

const LICENSE_KEY_DRAFT = "agencylens:signup:licenseKey";

type State =
  | { kind: "verifying" }
  | { kind: "needs-license-key"; email: string; token: string }
  | { kind: "error"; message: string }
  | { kind: "done" };

export function VerifyMagicLink() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "verifying" });
  const [licenseKey, setLicenseKey] = useState("");
  const [busy, setBusy] = useState(false);

  async function tryVerify(t: string, lk?: string) {
    try {
      const result = await verifyMagicLink(t, lk);
      setSession(result.jwt);
      setDek(result.dek);
      sessionStorage.removeItem(LICENSE_KEY_DRAFT);
      setState({ kind: "done" });
      navigate("/", { replace: true });
    } catch (e) {
      const ee = e as Error & { error?: string; email?: string; hint?: string };
      const code = ee.error ?? ee.message;
      if (code === "license_key_required_for_signup" && ee.email) {
        setState({ kind: "needs-license-key", email: ee.email, token: t });
      } else {
        setState({ kind: "error", message: prettyError(code, ee.hint) });
      }
    }
  }

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: "Missing sign-in token in URL." });
      return;
    }
    const draft = sessionStorage.getItem(LICENSE_KEY_DRAFT) ?? undefined;
    void tryVerify(token, draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onLicenseSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state.kind !== "needs-license-key") return;
    setBusy(true);
    setState({ kind: "verifying" });
    await tryVerify(state.token, licenseKey.trim());
    setBusy(false);
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-900">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-brand-700 text-white text-sm font-semibold">
            AL
          </span>
          <span className="text-lg font-semibold tracking-tight">AgencyLens</span>
        </Link>
      </div>

      {state.kind === "verifying" && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
          Verifying your sign-in link…
        </div>
      )}

      {state.kind === "needs-license-key" && (
        <form
          onSubmit={onLicenseSubmit}
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-6"
        >
          <h1 className="text-xl font-semibold tracking-tight">Welcome!</h1>
          <p className="text-sm text-slate-600">
            Finishing setting up <strong>{state.email}</strong>. Enter your
            AppSumo license key from your purchase email.
          </p>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">License key</span>
            <input
              type="text"
              required
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="AS-XXXXXXXX-XXXXX"
              autoComplete="off"
              autoFocus
            />
          </label>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Activating…" : "Activate AgencyLens"}
          </Button>
        </form>
      )}

      {state.kind === "error" && (
        <div className="space-y-4 rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-900">
          <div>
            <h2 className="text-base font-semibold">Sign-in link failed</h2>
            <p className="mt-2">{state.message}</p>
          </div>
          <Link
            to="/login"
            className="inline-block rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-900 hover:bg-red-100"
          >
            Request a new link
          </Link>
        </div>
      )}

      {state.kind === "done" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-900">
          Signed in. Redirecting to your dashboard…
        </div>
      )}
    </div>
  );
}

function prettyError(code: string, hint?: string): string {
  const base = (() => {
    switch (code) {
      case "invalid_or_expired_token":
        return "This sign-in link is invalid or has expired. Sign-in links work once and expire 15 minutes after they're sent.";
      case "missing_token":
        return "No sign-in token in the link.";
      case "invalid_license_key":
        return "We couldn't find that license key.";
      case "license_already_claimed":
        return "That license is already registered to a different email address.";
      default:
        return code.replace(/_/g, " ");
    }
  })();
  return hint ? `${base} — ${hint}` : base;
}
